import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, Columns3, Lock } from 'lucide-react';
import { cn } from '../../../utils/cn';
import Tooltip from '../../ui/Tooltip';
import { chooserColumns, columnLabel } from './columnModel';

const WIDTH = 288;
const GUTTER = 8;
const FOCUSABLE = 'input:not([disabled]), button:not([disabled])';

/** Fixed position under the trigger, kept inside the viewport (phones get the full width). */
const place = (trigger) => {
    const r = trigger.getBoundingClientRect();
    const width = Math.min(WIDTH, window.innerWidth - GUTTER * 2);
    const left = Math.max(GUTTER, Math.min(r.right - width, window.innerWidth - width - GUTTER));
    const top = r.bottom + 4;
    return { top, left, width, maxHeight: Math.max(160, window.innerHeight - top - GUTTER) };
};

/**
 * "Columns" toolbar button and its popover: a checkbox per column, move up / down, Reset.
 * Locked columns are shown checked and disabled. Columns an applied filter is on cannot be
 * hidden meanwhile (`forced`). Escape or an outside click closes it and focus returns to the
 * button; Tab stays inside while open.
 *
 * @param {object[]} columns all declared columns
 * @param {{order: string[], visible: string[]}} config
 * @param {string[]} forced column keys that must stay visible (filtered on)
 * @param {number} hiddenCount how many listed columns are currently hidden
 * @param {{toggle: Function, move: Function, reset: Function}} actions from useColumnPreference
 */
const ColumnChooser = ({ columns, config, forced, hiddenCount, actions }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const triggerRef = useRef(null);
    const popRef = useRef(null);
    const titleId = useId();

    const close = () => {
        setOpen(false);
        triggerRef.current?.focus();
    };

    useLayoutEffect(() => {
        if (!open) return undefined;
        const update = () => triggerRef.current && setPos(place(triggerRef.current));
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        popRef.current?.querySelector(FOCUSABLE)?.focus();
        const onMouseDown = (e) => {
            if (!popRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) setOpen(false);
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                close();
            } else if (e.key === 'Tab' && popRef.current) {
                const items = [...popRef.current.querySelectorAll(FOCUSABLE)];
                const first = items[0];
                const last = items[items.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last?.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first?.focus();
                }
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [open]);

    const listed = chooserColumns(columns);
    const movable = listed.filter((c) => !c.locked).map((c) => c.key);
    const shown = new Set([...config.visible, ...forced]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label={hiddenCount > 0 ? `Columns, ${hiddenCount} hidden` : 'Columns'}
                className={cn(
                    'inline-flex h-9 items-center gap-1.5 rounded-control border px-3 text-sm font-medium shadow-card outline-none focus-visible:ring-2 focus-visible:ring-focus',
                    hiddenCount > 0
                        ? 'border-primary-200 bg-primary-50 text-primary-800 hover:bg-primary-100'
                        : 'border-line bg-surface text-fg-secondary hover:bg-surface-muted'
                )}
            >
                <Columns3 className="h-4 w-4" aria-hidden="true" />
                Columns
                {hiddenCount > 0 && (
                    <span aria-hidden="true" className="text-xs font-normal tabular-nums">
                        ({hiddenCount} hidden)
                    </span>
                )}
            </button>
            {open &&
                pos &&
                createPortal(
                    <div
                        ref={popRef}
                        role="dialog"
                        aria-labelledby={titleId}
                        style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
                        className="fixed z-50 flex flex-col overflow-hidden rounded-card border border-line bg-surface shadow-overlay"
                    >
                        <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
                            <h2 id={titleId} className="text-sm font-semibold text-fg">
                                Columns
                            </h2>
                            <button
                                type="button"
                                onClick={actions.reset}
                                className="rounded px-1 text-sm font-medium text-primary-700 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-focus"
                            >
                                Reset to default
                            </button>
                        </div>
                        <ul className="min-h-0 flex-1 overflow-y-auto py-1">
                            {listed.map((col) => {
                                const label = columnLabel(col);
                                const pinned = col.locked === true;
                                const byFilter = !pinned && forced.includes(col.key);
                                const idx = movable.indexOf(col.key);
                                return (
                                    <li key={col.key} className="flex items-center gap-1 px-2 py-0.5">
                                        <label
                                            className={cn(
                                                'flex min-w-0 flex-1 items-center gap-2 rounded-control px-1 py-1.5 text-sm text-fg-secondary',
                                                pinned || byFilter ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-surface-muted'
                                            )}
                                            title={
                                                pinned
                                                    ? 'Always shown'
                                                    : byFilter
                                                      ? 'Shown while a filter on it is applied'
                                                      : undefined
                                            }
                                        >
                                            <input
                                                type="checkbox"
                                                checked={shown.has(col.key)}
                                                disabled={pinned || byFilter}
                                                onChange={() => actions.toggle(col.key)}
                                                className="h-4 w-4 rounded border-line accent-primary-600"
                                            />
                                            <span className="truncate">{label}</span>
                                            {(pinned || byFilter) && (
                                                <Lock className="h-3 w-3 shrink-0 text-fg-muted" aria-hidden="true" />
                                            )}
                                        </label>
                                        {!pinned && (
                                            <span className="flex shrink-0">
                                                <MoveButton
                                                    label={`Move ${label} earlier`}
                                                    disabled={idx <= 0}
                                                    onClick={() => actions.move(col.key, -1)}
                                                    icon={ArrowUp}
                                                />
                                                <MoveButton
                                                    label={`Move ${label} later`}
                                                    disabled={idx === movable.length - 1}
                                                    onClick={() => actions.move(col.key, 1)}
                                                    icon={ArrowDown}
                                                />
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>,
                    document.body
                )}
        </>
    );
};

const MoveButton = ({ label, disabled, onClick, icon: Icon }) => (
    <Tooltip label={label}>
        <button
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            className="grid h-7 w-7 place-content-center rounded-control text-fg-muted outline-none hover:bg-surface-muted hover:text-fg focus-visible:ring-2 focus-visible:ring-focus disabled:pointer-events-none disabled:opacity-30"
        >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
    </Tooltip>
);

export default ColumnChooser;
